import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/apiService';
import { academyApi } from '../../services/academyApi';
import type { AcademyTrack } from '@platform/shared-types';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * WHAT THIS TAB CAN AND CANNOT KNOW.
 *
 * Certification data now comes straight from academy-service's own progress
 * table (GET /admin/certifications -> progressRepo.listAllCertifications),
 * not from the `certification` mirror affiliate-service writes onto an
 * affiliate record at activation. That mirror is written ONLY at activation
 * (plus a refresh when an already-active partner later passes), so it could
 * never show a partner mid-course or one who had failed an attempt without
 * ever activating — "In training" and "Failed" were structurally always
 * zero, and there was never more than one certification's worth of data per
 * partner no matter how many certifications existed.
 *
 * The new endpoint returns one row per learner per certification, scoped
 * server-side to CERT# items only (attempts and chapter-progress rows are
 * excluded so no answer key can leave through it). That means this tab can
 * now show every certification a partner has actually PASSED — the one that
 * activates their referral link, any number of specialisations on top of it,
 * both, or neither — and every figure below is tallied straight from those
 * real rows.
 *
 * What it still cannot show: an admin bypass. affiliate-service's
 * activate.ts writes that status directly onto the affiliate record and
 * never touches academy-service's progress table (there is no exam attempt
 * behind it to record), so it is a different, non-exam path to activation.
 * Folding it back into "certified" here would reintroduce a number these
 * certification rows cannot actually support.
 */

interface AffiliateRow {
  id: string;
  email: string;
  name?: string;
  createdAt: number;
  status: string;
}

interface CertRow {
  learnerType: string;
  learnerId: string;
  trackId: string;
  status: string;
  bestScorePercent?: number;
  passedAt?: number;
}

/** How many rows affiliate-service's admin list returns per page (admin.ts listAffiliates). */
const PAGE_SIZE = 50;

export default function PartnersTab({ trackId }: { trackId: string }) {
  const [rows, setRows] = useState<AffiliateRow[]>([]);
  const [tracks, setTracks] = useState<AcademyTrack[]>([]);
  const [certs, setCerts] = useState<CertRow[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    // Three independent sources, merged client-side: the affiliate roster
    // (SuperAdminAffiliates.tsx:144 uses this exact call), the certification
    // catalogue (for titles and which one activates a partner), and every
    // certification row anyone has actually earned.
    Promise.all([
      api.affiliates.admin.list(),
      academyApi.listTracks(),
      academyApi.listCertifications(),
    ])
      .then(([affiliateRes, trackRes, certRes]: [any, any, any]) => {
        setRows(affiliateRes?.affiliates || affiliateRes?.items || []);
        // affiliate-service returns lastKey when more rows exist beyond this page.
        setHasMore(Boolean(affiliateRes?.lastKey));
        setTracks(trackRes?.tracks || []);
        setCerts(certRes?.certifications || []);
        setError('');
      })
      .catch((e: any) => setError(e.message || 'Could not load partners.'))
      .finally(() => setLoading(false));
  }, [trackId]);

  const trackTitle = (id: string) => tracks.find((t) => t.trackId === id)?.title || id;
  const activatingTrackId = tracks.find((t) => t.activatesPartner)?.trackId;

  // Every certification actually PASSED, indexed by learner email (lowercased —
  // academy-service's learnerId for an affiliate IS their claim email, see
  // requireLearner in academy-service's auth.ts).
  const certsByLearner = new Map<string, CertRow[]>();
  for (const c of certs) {
    if (c.status !== 'passed') continue;
    const key = c.learnerId.toLowerCase();
    const list = certsByLearner.get(key) || [];
    list.push(c);
    certsByLearner.set(key, list);
  }
  const passedFor = (r: AffiliateRow) => certsByLearner.get(r.email.toLowerCase()) || [];

  // Every tile counts ONLY the partners actually listed below, and every one of
  // them is derivable from a real, earned certification row.
  const certifiedActivating = rows.filter(
    (r) => activatingTrackId && passedFor(r).some((c) => c.trackId === activatingTrackId),
  ).length;
  const withSpecialisation = rows.filter(
    (r) => passedFor(r).some((c) => c.trackId !== activatingTrackId),
  ).length;
  const notCertified = rows.filter((r) => passedFor(r).length === 0).length;

  const tiles: Array<[string, number, string]> = [
    ['Partners listed', rows.length, ''],
    ['Certified on the activating certification', certifiedActivating, 'text-gray-900'],
    ['Holding at least one specialisation', withSpecialisation, 'text-gray-500'],
    ['Not yet certified', notCertified, 'text-gray-400'],
  ];

  return (
    <div className="space-y-4">
      {error && <p className="text-red-600 text-sm">{error}</p>}
      {loading && <RubyLoader variant="section" label="Loading partners…" />}

      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        {tiles.map(([label, value, tone]) => (
          <div key={label} className="bg-gray-50 rounded-2xl border border-gray-200 p-4">
            <p className="text-[11px] uppercase tracking-widest text-gray-500 font-bold">{label}</p>
            {/* A dash, not 0, when the fetch failed — a partner count of zero
                reads as "nobody is certified", which the data cannot support
                when it never actually loaded. */}
            <p className={`text-2xl font-extrabold mt-1 tabular-nums ${tone}`}>{error ? '—' : value}</p>
          </div>
        ))}
      </div>

      {!loading && !error && (
        <div className="bg-gray-50 rounded-2xl border border-gray-200 p-4 space-y-2">
          <p className="text-xs text-gray-400 leading-relaxed">
            <span className="font-bold text-gray-300">Every certification chip below is a real, earned pass</span>
            {' '}— pulled from academy-service&rsquo;s own progress records, one row per learner
            per certification. An admin bypass is a separate, non-exam path to activation
            recorded on the affiliate account itself, so it is not counted here. To see
            chapters completed and attempt history for one partner, open them with{' '}
            <span className="font-semibold text-gray-300">View</span>.
          </p>
          <p className="text-xs text-gray-500 leading-relaxed">
            {hasMore
              ? `Showing the first ${rows.length} partners — there are more. The counts above cover only the partners listed below, not the whole programme.`
              : `Showing ${rows.length} partner${rows.length === 1 ? '' : 's'} (up to ${PAGE_SIZE} per page). The counts above cover only the partners listed below.`}
          </p>
        </div>
      )}

      {!loading && rows.length === 0 && !error && (
        <div className="bg-gray-50 rounded-2xl border border-gray-200 p-8 text-center">
          <p className="text-sm text-gray-500">No partners registered yet.</p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="bg-gray-50 rounded-2xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm text-gray-700">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-gray-500">
                <th className="text-left p-3 font-bold">Partner</th>
                <th className="text-left p-3 font-bold">Registered</th>
                <th className="text-left p-3 font-bold">Certifications held</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const passed = passedFor(r);
                const activating = passed.find((c) => c.trackId === activatingTrackId);
                const specialisations = passed.filter((c) => c.trackId !== activatingTrackId);
                return (
                  <tr key={r.id} className="border-t border-gray-200">
                    <td className="p-3">
                      <p className="text-gray-900 font-semibold">{r.name || r.email}</p>
                      <p className="text-xs text-gray-500">{r.email}</p>
                    </td>
                    <td className="p-3 text-gray-400 tabular-nums">
                      {new Date(r.createdAt).toLocaleDateString()}
                    </td>
                    <td className="p-3">
                      {passed.length === 0 ? (
                        <span className="text-xs text-gray-400 italic">Not certified</span>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {activating && (
                            <span
                              title={`Activates this partner's link${activating.bestScorePercent != null ? ` · ${activating.bestScorePercent}%` : ''}${activating.passedAt ? ` · ${new Date(activating.passedAt).toLocaleDateString()}` : ''}`}
                              className="inline-flex items-center min-h-[28px] px-2.5 py-1 rounded-full text-[11px] font-bold bg-gray-900 text-white"
                            >
                              {trackTitle(activating.trackId)}
                            </span>
                          )}
                          {specialisations.map((c) => (
                            <span
                              key={c.trackId}
                              title={`${c.bestScorePercent != null ? `${c.bestScorePercent}% · ` : ''}${c.passedAt ? new Date(c.passedAt).toLocaleDateString() : ''}`}
                              className="inline-flex items-center min-h-[28px] px-2.5 py-1 rounded-full text-[11px] font-bold bg-gray-100 border border-gray-300 text-gray-700"
                            >
                              {trackTitle(c.trackId)}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <Link
                        to={`/affiliates/${r.id}`}
                        className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-lg border border-gray-300 text-xs font-semibold text-gray-700 hover:border-gray-900 hover:text-gray-900 transition"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
